import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { AppInformationService, AuthService, GenesisUnitOfWork, ReportService } from 'app/core/core.module';
import { PurchaseOrderLine } from 'app/core/model/purchase-order-line';
import { PurchaseOrderPickUp } from 'app/core/model/purchase-order-pick-up';
import { PurchaseOrderPickUpLine } from 'app/core/model/purchase-order-pick-up-line';
import { Vendor } from 'app/core/model/vendor';
import validationEngine from 'devextreme/ui/validation_engine';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-vendor-pickup-dialog',
    templateUrl: './vendor-pickup-dialog.component.html',
    styleUrls: ['./vendor-pickup-dialog.component.scss'],
    standalone: false
})
export class VendorPickupDialogComponent implements OnInit {
    public modalRef = inject(BsModalRef);
    uow = inject(GenesisUnitOfWork);
    auth = inject(AuthService);
    appInfo = inject(AppInformationService);
    reportService = inject(ReportService);
    ref = inject(ChangeDetectorRef);

    result = new Subject<{ success: boolean }>();
    pickUp: PurchaseOrderPickUp;
    pickUpLines: PurchaseOrderPickUpLine[] = [];
    isNew: boolean;
    purchaseOrderLines: PurchaseOrderLine[];
    vendor: Vendor;
    canUnComplete: boolean = false;

    async ngOnInit() {
        this.canUnComplete = this.auth.hasRole('PurchaseOrder:CanUnCompleteVendorPickup');

        if (!this.pickUp) {
            this.vendor = this.purchaseOrderLines[0]?.purchaseOrder?.vendor;

            const date = new Date();
            this.pickUp = this.uow.purchaseOrderPickUps.createEntity({
                scheduledDate: null,
                created: date,
                createdBy: this.auth.currentUser.userName,
                instructions: this.vendor.notes ?? null,
            }) as PurchaseOrderPickUp;
            this.purchaseOrderLines.forEach((line) => {
                const pickUpLine = this.uow.purchaseOrderPickUpLines.createEntity({
                    purchaseOrderPickUpId: this.pickUp.id,
                    purchaseOrderLineId: line.id,
                    created: date,
                    createdBy: this.auth.currentUser.userName,
                }) as PurchaseOrderPickUpLine;
                pickUpLine.quantityScheduled = line.pickUpQuantityScheduled;
                pickUpLine.quantityReadyToPickUp = line.quantity - line.pickUpQuantityScheduled;
                pickUpLine.quantity = pickUpLine.quantityReadyToPickUp;
                this.pickUpLines.push(pickUpLine);
            });
            this.isNew = true;
        } else {
            this.pickUpLines = this.pickUp.lines.filter((line) => !line.deleted);
            const vendorId = this.pickUp.lines[0]?.purchaseOrderLine?.purchaseOrder?.vendorId;
            if (vendorId) this.vendor = (await this.uow.vendors.byId(vendorId)) as Vendor;
        }
    }

    remove = (pickUpLine: PurchaseOrderPickUpLine) => {
        this.pickUpLines = this.pickUpLines.filter((line) => line.id !== pickUpLine.id);
        pickUpLine.deleted = new Date();
        pickUpLine.deletedBy = this.auth.currentUser.userName;
    };

    async save() {
        if (this.isNew) this.purgeEmptyLines();
        const success = this.validate();
        if (!success) return;

        await this.uow.commit();
        this.result.next({ success: true });
        this.modalRef.hide();
    }

    cancel() {
        this.uow.rollback();
        this.result.next({ success: false });
        this.modalRef.hide();
    }

    validate() {
        if (!this.pickUpLines || this.pickUpLines.length === 0) {
            this.appInfo.notifyToast('A Vendor Pickup must have at least one pickup line', 'error');
            return false;
        }

        const groupConfig = validationEngine.getGroupConfig('pickUpValidationGroup');
        if (groupConfig) {
            const result = validationEngine.validateGroup('pickUpValidationGroup');
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\r\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before saving.\r\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }

    async print() {
        if (this.isNew) this.purgeEmptyLines();
        const success = this.validate();
        if (!success) return;
        await this.uow.commit();

        this.reportService.printVendorPickUp(this.pickUp.id);
    }

    purgeEmptyLines() {
        this.pickUpLines.filter((line) => !line.quantity).forEach((line) => line.entityAspect.rejectChanges());
        this.pickUpLines = this.pickUpLines.filter((line) => !!line.quantity);

        this.ref.detectChanges(); // it was validating lines before they were removed. This ensures the screen is re-painted before validating
    }

    unComplete() {
        this.pickUp.completed = null;
        this.pickUp.completedBy = null;
        this.pickUpLines.forEach((line) => {
            line.completed = null;
            line.completedBy = null;
        });
    }
}
